import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, catchError, of, switchMap } from 'rxjs';
import { TtoCampaignVideoDto } from '@/app/common/general-types/tiktok-one-reporting.dto';
import { GetSparkCampaignForBriefAdGroupDto } from '@/app/common/general-types/tiktok-spark-ads.dto';
import {
  SparkAuthStatusDto,
  SparkCreatorVideoDto,
} from '@/app/common/general-types/spark-ads-v3.dto';
import { TiktokSparkAdsService } from '@/app/services/tiktok-spark-ads/tiktok-spark-ads.service';
import { ManageBriefTabsService } from '@/app/pages/manage-brief-tabs/service/manage-brief-tabs/manage-brief-tabs.service';
import { SparkAuthService } from '@/app/pages/manage-brief-tabs/tabs/spark-ads/services/spark-auth.service';
import { SparkAdsReportingService } from '@/app/pages/manage-brief-tabs/tabs/spark-ads/services/spark-ads-reporting.service';
import { SparkAdsStore } from '@/app/pages/manage-brief-tabs/tabs/spark-ads/services/spark-ads-store';
import { SparkLoadState } from '@/app/pages/manage-brief-tabs/tabs/spark-ads/spark-ads.types';
import { formatCurrency } from '@/app/pages/manage-brief-tabs/tabs/spark-ads/spark-ads.format';

/** What the panel is doing, so buttons show progress and stay disabled meanwhile. */
type BoostAction = 'idle' | 'requesting' | 'applying' | 'sparking';

/**
 * Spark authorisation and budget for one creator post.
 *
 * Step 1 gets the creator's permission — a Spark ad runs from their handle, so the post
 * cannot carry budget without it. Step 2 stays locked, and says why, until it is live.
 */
@Component({
  selector: 'vat-analysis-boosting-v2',
  imports: [],
  templateUrl: './analysis-boosting-v2.html',
  styleUrl: './analysis-boosting-v2.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisBoostingV2Component {
  readonly video = input.required<TtoCampaignVideoDto>();

  private readonly authService = inject(SparkAuthService);
  private readonly reportingService = inject(SparkAdsReportingService);
  private readonly sparkAdsService = inject(TiktokSparkAdsService);
  private readonly manageBriefTabsService = inject(ManageBriefTabsService);
  private readonly store = inject(SparkAdsStore);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly authState = signal<SparkLoadState>('idle');
  protected readonly auth = signal<SparkAuthStatusDto | undefined>(undefined);
  protected readonly actionError = signal<string | null>(null);
  protected readonly action = signal<BoostAction>('idle');

  /** Shown when the creator supplied a code outside the app. */
  protected readonly showCodeEntry = signal(false);
  protected readonly pastedCode = signal('');

  protected readonly adGroups = signal<GetSparkCampaignForBriefAdGroupDto[]>([]);
  protected readonly selectedAdGroupId = signal<string | undefined>(undefined);
  protected readonly advertiserId = signal<string | undefined>(undefined);

  /** Set once this post has been added as a Spark ad in this session. */
  protected readonly sparkedAdGroupName = signal<string | undefined>(undefined);

  /** Library entry for this post, which carries the moderation verdict. */
  protected readonly libraryEntry = signal<SparkCreatorVideoDto | undefined>(undefined);

  private readonly loadAuth$ = new Subject<void>();

  protected readonly videoId = computed(() => this.video().video_info?.video_id ?? '');

  protected readonly handle = computed(() => {
    const handle = this.video().creator_info?.handle_name;
    if (!handle) {
      return 'this creator';
    }
    return handle.startsWith('@') ? handle : `@${handle}`;
  });

  protected readonly briefId = computed(() => this.manageBriefTabsService.focussedBrief()?.id);

  protected readonly isAuthorised = computed(() => this.auth()?.state === 'authorised');
  protected readonly isRequested = computed(() => this.auth()?.state === 'requested');
  protected readonly isExpired = computed(() => this.auth()?.state === 'expired');
  protected readonly isRejected = computed(() => this.auth()?.state === 'rejected');
  protected readonly isUnknown = computed(() => this.auth()?.state === 'unknown');
  protected readonly isNotRequested = computed(() => this.auth()?.state === 'none');

  protected readonly expiryLabel = computed(() => {
    const auth = this.auth();
    if (!auth?.expiresAt) {
      return null;
    }
    const expires = new Date(auth.expiresAt).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const days = auth.daysUntilExpiry;
    if (days === null) {
      return `Expires ${expires}.`;
    }
    if (days <= 0) {
      return `Expired ${expires}.`;
    }
    return `Expires ${expires} — ${days} ${days === 1 ? 'day' : 'days'} left.`;
  });

  protected readonly moderationStatus = computed(
    () => this.libraryEntry()?.moderation.status ?? 'unknown',
  );

  protected readonly moderationReasons = computed(
    () => this.libraryEntry()?.moderation.reasons ?? [],
  );

  protected readonly alreadySparked = computed(
    () => this.libraryEntry()?.isSparked === true || this.sparkedAdGroupName() !== undefined,
  );

  protected readonly hasAdGroups = computed(() => this.adGroups().length > 0);

  protected readonly canSpark = computed(
    () =>
      this.isAuthorised() &&
      this.hasAdGroups() &&
      this.selectedAdGroupId() !== undefined &&
      this.action() === 'idle' &&
      !this.alreadySparked(),
  );

  /** Step 2 is locked until permission is live — and states why, not just greyed out. */
  protected readonly step2LockedReason = computed(() => {
    if (this.isAuthorised()) {
      return null;
    }
    switch (this.auth()?.state) {
      case 'expired':
        return 'The creator’s permission has expired — request a renewal first.';
      case 'rejected':
        return 'The creator declined the request — ask them again before this post can carry budget.';
      case 'unknown':
        return 'We could not reach TikTok to check this post’s permission.';
      default:
        return 'Available once the creator authorises the post.';
    }
  });

  protected readonly isBusy = computed(() => this.action() !== 'idle');

  constructor() {
    this.loadAuth$
      .pipe(
        switchMap(() => {
          const videoId = this.videoId();
          if (!videoId) {
            return of(undefined);
          }
          this.authState.set('loading');
          return this.authService.getStatus(videoId, { briefId: this.briefId() }).pipe(
            catchError((error: unknown) => {
              this.actionError.set(this.messageFor(error));
              this.authState.set('error');
              return of(undefined);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((status) => {
        if (status) {
          this.auth.set(status);
          this.authState.set('loaded');
        }
      });

    queueMicrotask(() => {
      this.loadAuth$.next();
      this.loadCampaignContext();
      this.loadLibraryEntry();
    });
  }

  // --- Step 1 ---------------------------------------------------------------

  protected requestAuth(): void {
    const videoId = this.videoId();
    if (!videoId || this.isBusy()) {
      return;
    }

    this.action.set('requesting');
    this.actionError.set(null);

    this.authService
      .requestAuth({ videoId, briefId: this.briefId() })
      .pipe(
        catchError((error: unknown) => {
          this.actionError.set(this.messageFor(error));
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((status) => {
        this.action.set('idle');
        if (status) {
          this.auth.set(status);
        }
      });
  }

  protected toggleCodeEntry(): void {
    this.showCodeEntry.update((open) => !open);
  }

  protected setPastedCode(value: string): void {
    this.pastedCode.set(value);
  }

  /**
   * Applies a code the creator sent another way.
   *
   * TikTok ties an authorisation to an advertiser account, so this needs the ad account
   * the brief's campaign spends from.
   */
  protected applyCode(): void {
    const code = this.pastedCode().trim();
    const advertiserId = this.advertiserId();
    const videoId = this.videoId();

    if (!code || this.isBusy()) {
      return;
    }
    if (!advertiserId) {
      this.actionError.set(
        'We need to know which TikTok ad account to attach this code to. Create the campaign first.',
      );
      return;
    }

    this.action.set('applying');
    this.actionError.set(null);

    this.authService
      .applyCode({ sparkCode: code, advertiserId, videoId, briefId: this.briefId() })
      .pipe(
        catchError((error: unknown) => {
          this.actionError.set(this.messageFor(error));
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.action.set('idle');
        if (result && 'state' in result) {
          this.auth.set(result);
          this.showCodeEntry.set(false);
          this.pastedCode.set('');
        }
      });
  }

  protected copyCode(): void {
    const code = this.auth()?.authCode;
    if (code) {
      void navigator.clipboard?.writeText(code);
    }
  }

  // --- Step 2 ---------------------------------------------------------------

  protected selectAdGroup(adGroupId: string): void {
    this.selectedAdGroupId.set(adGroupId);
  }

  protected adGroupRowClass(adGroupId: string): string {
    return this.selectedAdGroupId() === adGroupId
      ? 'border-2 border-brand-studio bg-brand-studio/5'
      : 'border border-border bg-card hover:border-brand-studio/40';
  }

  protected adGroupMeta(adGroup: GetSparkCampaignForBriefAdGroupDto): string {
    const currency =
      this.manageBriefTabsService.focussedBrief()?.currentPrice?.currencyCode ?? null;
    const budget = adGroup.budget ? formatCurrency(adGroup.budget, currency, 0) : null;
    const mode = adGroup.budgetMode === 'BUDGET_MODE_DAY' ? 'per day' : 'lifetime';
    return budget ? `${budget} ${mode}` : 'Budget not set';
  }

  /** Creates the Spark ad against the chosen ad group. */
  protected sparkPost(): void {
    const adGroupId = this.selectedAdGroupId();
    const advertiserId = this.advertiserId();
    const videoId = this.videoId();

    if (!adGroupId || !advertiserId || !videoId || !this.canSpark()) {
      return;
    }

    const adGroup = this.adGroups().find((group) => group.tiktokAdGroupId === adGroupId);

    this.action.set('sparking');
    this.actionError.set(null);

    this.sparkAdsService
      .createSparkAd({
        ad_values: {
          ad_name: `${adGroup?.adgroupName ?? 'Spark ad'} · ${this.handle()}`,
          ad_format: 'SINGLE_VIDEO',
          // The ad runs from the creator's own handle.
          identity_type: 'AUTH_CODE',
        },
        shout_office_use: {
          video_id: videoId,
          advertiser_id: advertiserId,
          adgroup_id: adGroupId,
        },
      })
      .pipe(
        catchError((error: unknown) => {
          this.actionError.set(this.messageFor(error));
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.action.set('idle');
        if (response) {
          this.sparkedAdGroupName.set(adGroup?.adgroupName ?? 'your ad group');
        }
      });
  }

  /** Sends the user to the builder carrying this post, so it arrives preselected. */
  protected createAdGroupForThisPost(): void {
    const videoId = this.videoId();
    if (videoId) {
      this.store.carryVideoToBuilder(videoId);
    }
    this.store.setView('new');
  }

  protected goToDashboard(): void {
    this.store.setView('dashboard');
  }

  protected retryAuth(): void {
    this.loadAuth$.next();
  }

  // --- Internals ------------------------------------------------------------

  /**
   * Loads the brief's campaign so Step 2 knows which ad groups exist and which ad
   * account an authorisation should be applied to.
   */
  private loadCampaignContext(): void {
    const briefId = this.briefId();
    if (!briefId) {
      return;
    }

    this.sparkAdsService
      .getSparkCampaignForBrief(briefId)
      .pipe(
        // No campaign yet is the normal first-run case, not an error.
        catchError(() => of(undefined)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((campaign) => {
        if (!campaign) {
          return;
        }
        this.advertiserId.set(campaign.advertiserId ?? undefined);
        const groups = campaign.tiktokSparkAdGroups ?? [];
        this.adGroups.set(groups);
        if (groups.length > 0 && !this.selectedAdGroupId()) {
          this.selectedAdGroupId.set(groups[0].tiktokAdGroupId ?? undefined);
        }
      });
  }

  /** The library payload carries the moderation verdict and whether this post is sparked. */
  private loadLibraryEntry(): void {
    const briefId = this.briefId();
    if (!briefId) {
      return;
    }

    this.reportingService
      .getCreatorVideos(briefId)
      .pipe(
        catchError(() => of(undefined)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((library) => {
        const entry = library?.videos.find((item) => item.videoId === this.videoId());
        if (entry) {
          this.libraryEntry.set(entry);
        }
      });
  }

  private messageFor(error: unknown): string {
    const candidate = error as { error?: { message?: string }; message?: string };
    return candidate?.error?.message ?? candidate?.message ?? 'Something went wrong.';
  }
}
